import * as React from 'react';
import styled, { css } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';

import { composeRefs } from '../../internals/composeRefs';
import type { FloatingAlign, FloatingSide } from '../../internals/computeFloatingPosition';
import { createUIContext } from '../../internals/createContext';
import { getSemanticAttributes } from '../../internals/semanticProps';
import { Portal } from '../../internals/Portal';
import { Slot } from '../../internals/Slot';
import { useClickOutside } from '../../internals/useClickOutside';
import { useControllable } from '../../internals/useControllable';
import { useEscapeKey } from '../../internals/useEscapeKey';
import { useFloatingPosition } from '../../internals/useFloatingPosition';
import { useId } from '../../internals/useId';
import { useIsomorphicLayoutEffect } from '../../internals/useIsomorphicLayoutEffect';
import { Spinner } from '../../primitives/Spinner/Spinner';
import { palette } from '../../tokens/palette';
import { rem } from '../../tokens/spacing';
import { zIndex } from '../../tokens/zIndex';

/* ─────────────────────────── Types ─────────────────────────────────────── */

export type DropdownItemColor = 'default' | 'danger' | 'warning' | 'success';

/* ─────────────────────────── Context ───────────────────────────────────── */

export interface DropdownRootProps {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  side?: FloatingSide;
  align?: FloatingAlign;
  /** Viewport coordinates for a virtual cursor anchor. Allows a controlled Root without Trigger. */
  anchorPoint?: { x: number; y: number } | null;
  children: React.ReactNode;
}

interface DropdownContextValue {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  triggerRef: React.MutableRefObject<HTMLElement | null>;
  contentRef: React.MutableRefObject<HTMLDivElement | null>;
  menuId: string;
  triggerId: string;
  highlightedId: string | null;
  setHighlightedId: (id: string | null) => void;
  side: FloatingSide;
  align: FloatingAlign;
  anchorPoint: { x: number; y: number } | null;
}

const [DropdownProvider, useDropdownContext] = createUIContext<DropdownContextValue>('Dropdown');

/* ─────────────────────────── Root ──────────────────────────────────────── */

const DropdownRoot = ({
  open: openProp,
  defaultOpen,
  onOpenChange,
  side = 'bottom',
  align = 'end',
  anchorPoint = null,
  children,
}: DropdownRootProps) => {
  const [open, setOpen] = useControllable<boolean>({
    value: openProp,
    defaultValue: defaultOpen ?? false,
    onChange: onOpenChange,
  });
  const triggerRef = React.useRef<HTMLElement | null>(null);
  const contentRef = React.useRef<HTMLDivElement | null>(null);
  const menuId = useId('dropdown-menu');
  const triggerId = useId('dropdown-trigger');
  const [highlightedId, setHighlightedId] = React.useState<string | null>(null);

  const value = React.useMemo(
    () => ({
      open,
      onOpenChange: setOpen,
      triggerRef,
      contentRef,
      menuId,
      triggerId,
      highlightedId,
      setHighlightedId,
      side,
      align,
      anchorPoint,
    }),
    [open, setOpen, menuId, triggerId, highlightedId, side, align, anchorPoint],
  );

  return <DropdownProvider value={value}>{children}</DropdownProvider>;
};

DropdownRoot.displayName = 'Dropdown.Root';

/* ─────────────────────────── Trigger ──────────────────────────────────── */

export interface DropdownTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean;
  semanticId?: string;
}

function ChevronDownIcon() {
  return (
    <svg aria-hidden width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path
        d="M4 6L8 10L12 6"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const StyledTrigger = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: ${rem(8)};
  padding: ${rem(8)} ${rem(12)};
  border: 1px solid var(--ui-border, ${palette.LightGray});
  border-radius: var(--ui-radius-md, ${rem(6)});
  background: var(--ui-surface, ${palette.White});
  color: var(--ui-text, ${palette.Black});
  font-family: var(--ui-font-sans, 'HK Grotesk', sans-serif);
  font-size: ${rem(14)};
  cursor: pointer;
  transition: all 0.2s ease-in-out;

  &:hover {
    border-color: var(--ui-text-subtle, ${palette.SpanishGray});
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const DropdownTrigger = React.forwardRef<HTMLButtonElement, DropdownTriggerProps>(
  function DropdownTrigger(
    { asChild = false, semanticId, onClick, type = 'button', children, ...rest },
    ref,
  ) {
    const { open, onOpenChange, triggerRef, menuId, triggerId } = useDropdownContext();
    const semanticProps = getSemanticAttributes(semanticId);
    const composedRef = React.useMemo(() => composeRefs(ref, triggerRef), [ref, triggerRef]);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      onClick?.(event);
      if (!event.defaultPrevented) {
        onOpenChange(!open);
      }
    };

    if (asChild) {
      return (
        <Slot
          ref={composedRef as React.Ref<HTMLElement>}
          id={triggerId}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-controls={menuId}
          data-state={open ? 'open' : 'closed'}
          onClick={handleClick as React.MouseEventHandler<HTMLElement>}
          {...semanticProps}
          {...(rest as unknown as React.ComponentProps<typeof Slot>)}
        >
          {children}
        </Slot>
      );
    }

    return (
      <StyledTrigger
        ref={composedRef}
        id={triggerId}
        type={type}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        data-state={open ? 'open' : 'closed'}
        onClick={handleClick}
        {...semanticProps}
        {...rest}
      >
        {children}
        <ChevronDownIcon />
      </StyledTrigger>
    );
  },
);

DropdownTrigger.displayName = 'Dropdown.Trigger';

/* ─────────────────────────── Content ──────────────────────────────────── */

const DROPDOWN_SHADOW = `0 ${rem(2)} ${rem(20)} rgba(109, 109, 109, 0.15)`;

const StyledMenu = styled.div`
  position: relative;
  background: var(--ui-surface, ${palette.White});
  color: var(--ui-text, ${palette.Black});
  border-radius: var(--ui-radius-md, ${rem(6)});
  box-shadow: ${DROPDOWN_SHADOW};
  font-family: var(--ui-font-sans, 'HK Grotesk', sans-serif);
`;

const menuMotionVariants = {
  hidden: {
    height: 0,
    opacity: 0,
    overflow: 'hidden' as const,
  },
  visible: {
    height: 'auto',
    opacity: 1,
    overflow: 'visible' as const,
    transition: {
      height: { duration: 0.4 },
      opacity: { duration: 0.2, delay: 0.1 },
    },
  },
  exit: {
    height: 0,
    opacity: 0,
    overflow: 'hidden' as const,
    transition: {
      height: { duration: 0.4 },
      opacity: { duration: 0.25 },
    },
  },
};

const StyledMenuInner = styled.div`
  padding: ${rem(10)} 0;
`;

function getEnabledMenuItems(root: HTMLElement | null): HTMLElement[] {
  if (!root) return [];
  return Array.from(root.querySelectorAll<HTMLElement>('[role="menuitem"]')).filter(
    (el) => el.dataset.disabled !== 'true',
  );
}

export interface DropdownContentProps extends React.HTMLAttributes<HTMLDivElement> {
  semanticId?: string;
  /** When true, raises menu above modals/drawers (e.g. import menus inside stacked UI). */
  elevated?: boolean;
}

const DropdownContent = React.forwardRef<HTMLDivElement, DropdownContentProps>(
  function DropdownContent(
    { semanticId, elevated = false, style, onKeyDown, children, ...rest },
    ref,
  ) {
    const {
      open,
      onOpenChange,
      triggerRef,
      contentRef,
      menuId,
      triggerId,
      highlightedId,
      setHighlightedId,
      side,
      align,
      anchorPoint,
    } = useDropdownContext();
    const semanticProps = getSemanticAttributes(semanticId);

    const close = React.useCallback(() => onOpenChange(false), [onOpenChange]);

    useClickOutside(contentRef, close, open, { ignoredRefs: [triggerRef] });
    useEscapeKey(() => {
      close();
      setHighlightedId(null);
      triggerRef.current?.focus();
    }, open);

    const { coords } = useFloatingPosition({
      open,
      side,
      align,
      offsetPx: 0,
      anchorPoint,
      triggerRef,
      floatingRef: contentRef,
    });

    useIsomorphicLayoutEffect(() => {
      if (!open) {
        setHighlightedId(null);
      }
      return undefined;
    }, [open, setHighlightedId]);

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
      onKeyDown?.(event);
      if (event.defaultPrevented) return;
      const host = contentRef.current;
      const items = getEnabledMenuItems(host);
      if (items.length === 0) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        setHighlightedId(null);
        triggerRef.current?.focus();
        return;
      }

      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const ids = items.map((el) => el.id).filter(Boolean);
        const currentIdx = highlightedId ? ids.indexOf(highlightedId) : -1;
        const delta = event.key === 'ArrowDown' ? 1 : -1;
        const nextIdx = (currentIdx + delta + ids.length) % ids.length;
        setHighlightedId(ids[nextIdx] ?? null);
        return;
      }

      if (event.key === 'Enter') {
        event.preventDefault();
        const target = highlightedId
          ? items.find((el) => el.id === highlightedId)
          : items[0];
        target?.click();
      }
    };

    const composedRef = React.useMemo(() => composeRefs(ref, contentRef), [ref, contentRef]);

    return (
      <Portal>
        <AnimatePresence initial={false} mode="wait">
          {open && (
            <motion.div
              key={menuId}
              variants={menuMotionVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              onClick={(e) => e.stopPropagation()}
              style={{
                position: 'fixed',
                zIndex: elevated ? zIndex.loading : zIndex.tooltip,
                top: coords.top,
                left: coords.left,
              }}
            >
              <StyledMenu
                ref={composedRef}
                id={menuId}
                role="menu"
                aria-labelledby={triggerRef.current ? triggerId : undefined}
                tabIndex={-1}
                data-state={open ? 'open' : 'closed'}
                data-side={side}
                data-align={align}
                style={style}
                onKeyDown={handleKeyDown}
                {...semanticProps}
                {...rest}
              >
                <StyledMenuInner>{children}</StyledMenuInner>
              </StyledMenu>
            </motion.div>
          )}
        </AnimatePresence>
      </Portal>
    );
  },
);

DropdownContent.displayName = 'Dropdown.Content';

/* ─────────────────────────── CloseButton ──────────────────────────────── */

const StyledCloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: ${rem(-10)};
  right: ${rem(-10)};
  width: ${rem(20)};
  height: ${rem(20)};
  padding: ${rem(6)};
  border-radius: 50%;
  border: none;
  background: var(--ui-surface, ${palette.White});
  box-shadow: ${DROPDOWN_SHADOW};
  cursor: pointer;
  z-index: 1;

  .material-symbols-outlined {
    font-size: ${rem(10)};
    line-height: ${rem(8)};
    color: var(--ui-interactive, ${palette.NewPurple});
  }
`;

export interface DropdownCloseButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  semanticId?: string;
}

const DropdownCloseButton = React.forwardRef<HTMLButtonElement, DropdownCloseButtonProps>(
  function DropdownCloseButton({ semanticId, onClick, type = 'button', ...rest }, ref) {
    const { onOpenChange } = useDropdownContext();
    const semanticProps = getSemanticAttributes(semanticId);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      onClick?.(event);
      if (!event.defaultPrevented) onOpenChange(false);
    };

    return (
      <StyledCloseButton
        ref={ref}
        type={type}
        aria-label="Close menu"
        onClick={handleClick}
        {...semanticProps}
        {...rest}
      >
        <span className="material-symbols-outlined">close</span>
      </StyledCloseButton>
    );
  },
);

DropdownCloseButton.displayName = 'Dropdown.CloseButton';

/* ─────────────────────────── Item color tokens ────────────────────────── */

const ITEM_COLORS: Record<DropdownItemColor, { color: string; hoverColor: string }> = {
  default: {
    color: `var(--ui-text, ${palette.Black})`,
    hoverColor: `var(--ui-text, ${palette.Black})`,
  },
  danger: {
    color: `var(--ui-danger, ${palette.Red})`,
    hoverColor: `var(--ui-danger, ${palette.Red})`,
  },
  warning: {
    color: `var(--ui-warning, ${palette.amber[600]})`,
    hoverColor: `var(--ui-warning, ${palette.amber[600]})`,
  },
  success: {
    color: `var(--ui-success, ${palette.Green})`,
    hoverColor: `var(--ui-success, ${palette.Green})`,
  },
};

/* ─────────────────────────── Item ─────────────────────────────────────── */

const StyledIconBox = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: ${rem(20)};
  height: ${rem(20)};

  .material-symbols-outlined {
    font-size: ${rem(18)};
    line-height: 1;
  }

  svg {
    width: ${rem(18)};
    height: ${rem(18)};
  }
`;

const StyledItemInner = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(8)};
  width: 100%;
`;

const StyledItemText = styled.div`
  flex: 1;
  min-width: 0;
`;

const StyledItemDescription = styled.div`
  font-size: ${rem(12)};
  line-height: ${rem(16)};
  color: var(--ui-text-subtle, ${palette.SpanishGray});
  font-weight: 400;
  margin-top: ${rem(2)};
`;

const StyledShortcut = styled.span`
  margin-left: auto;
  padding-left: ${rem(16)};
  font-size: ${rem(11)};
  line-height: 1;
  color: var(--ui-text-subtle, ${palette.SpanishGray});
  font-weight: 400;
  letter-spacing: 0.5px;
  flex-shrink: 0;
`;

const StyledItemRow = styled.div<{ $disabled?: boolean; $color: DropdownItemColor }>`
  display: flex;
  align-items: center;
  min-width: ${rem(140)};
  padding: ${rem(8)};
  margin: 0 ${rem(10)};
  border-radius: var(--ui-radius-sm, ${rem(4)});
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  outline: none;
  font-size: ${rem(14)};
  font-weight: 400;
  text-align: left;
  transition: all 0.2s ease-in-out;

  ${({ $color }) => css`
    color: ${ITEM_COLORS[$color].color};

    ${StyledIconBox} {
      color: ${ITEM_COLORS[$color].color};
    }
  `}

  &[data-highlighted='true'] {
    background: var(--ui-background-muted, ${palette.OffWhite});

    ${({ $color }) => css`
      color: ${ITEM_COLORS[$color].hoverColor};

      ${StyledIconBox} {
        color: ${ITEM_COLORS[$color].hoverColor};
      }
    `}
  }
`;

const StyledItemLoader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: ${rem(140)};
  padding: ${rem(10)} ${rem(5)};
  margin: 0 ${rem(10)};
`;

export interface DropdownItemProps extends React.HTMLAttributes<HTMLDivElement> {
  onSelect?: (event: React.SyntheticEvent) => void;
  disabled?: boolean;
  loading?: boolean;
  /** When true, the dropdown stays open after this item is selected. Useful for async actions. */
  closeOnSelect?: boolean;
  icon?: React.ReactNode;
  /** Semantic color for the item text and icon. */
  color?: DropdownItemColor;
  /** Secondary description text below the label. */
  description?: string;
  /** Keyboard shortcut hint displayed on the right side. */
  shortcut?: string;
  semanticId?: string;
}

const DropdownItem = React.forwardRef<HTMLDivElement, DropdownItemProps>(function DropdownItem(
  {
    onSelect,
    disabled = false,
    loading = false,
    closeOnSelect = true,
    icon,
    color = 'default',
    description,
    shortcut,
    semanticId,
    onClick,
    id: idProp,
    onMouseEnter,
    children,
    ...rest
  },
  ref,
) {
  const { highlightedId, setHighlightedId, onOpenChange } = useDropdownContext();
  const autoId = useId('menuitem');
  const id = idProp ?? autoId;
  const semanticProps = getSemanticAttributes(semanticId);
  const isHighlighted = !disabled && !loading && highlightedId === id;

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    onClick?.(event);
    if (disabled || loading || event.defaultPrevented) return;
    onSelect?.(event);
    if (closeOnSelect) {
      onOpenChange(false);
      setHighlightedId(null);
    }
  };

  const handleMouseEnter = (event: React.MouseEvent<HTMLDivElement>) => {
    onMouseEnter?.(event);
    if (!disabled && !loading) {
      setHighlightedId(id);
    }
  };

  const handleMouseLeave = () => {
    if (isHighlighted) {
      setHighlightedId(null);
    }
  };

  if (loading) {
    return (
      <StyledItemLoader role="menuitem" aria-disabled="true" id={id}>
        <Spinner size="sm" />
      </StyledItemLoader>
    );
  }

  return (
    <StyledItemRow
      ref={ref}
      id={id}
      role="menuitem"
      tabIndex={-1}
      data-disabled={disabled ? 'true' : 'false'}
      data-highlighted={isHighlighted ? 'true' : 'false'}
      $disabled={disabled}
      $color={color}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
      {...semanticProps}
      {...rest}
    >
      <StyledItemInner>
        {icon ? <StyledIconBox aria-hidden>{icon}</StyledIconBox> : null}
        <StyledItemText>
          <span>{children}</span>
          {description ? (
            <StyledItemDescription>{description}</StyledItemDescription>
          ) : null}
        </StyledItemText>
        {shortcut ? <StyledShortcut aria-hidden>{shortcut}</StyledShortcut> : null}
      </StyledItemInner>
    </StyledItemRow>
  );
});

DropdownItem.displayName = 'Dropdown.Item';

/* ─────────────────────────── Separator ─────────────────────────────────── */

const StyledSeparator = styled.div`
  height: 1px;
  margin: ${rem(4)} ${rem(10)};
  background: var(--ui-border, ${palette.LightGray});
`;

export interface DropdownSeparatorProps extends React.HTMLAttributes<HTMLDivElement> {
  semanticId?: string;
}

const DropdownSeparator = React.forwardRef<HTMLDivElement, DropdownSeparatorProps>(
  function DropdownSeparator({ semanticId, ...rest }, ref) {
    const semanticProps = getSemanticAttributes(semanticId);
    return <StyledSeparator ref={ref} role="separator" {...semanticProps} {...rest} />;
  },
);

DropdownSeparator.displayName = 'Dropdown.Separator';

/* ─────────────────────────── Group ─────────────────────────────────────── */

const StyledGroup = styled.div`
  padding: ${rem(4)} 0;
`;

export interface DropdownGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  semanticId?: string;
}

const DropdownGroup = React.forwardRef<HTMLDivElement, DropdownGroupProps>(
  function DropdownGroup({ semanticId, ...rest }, ref) {
    const semanticProps = getSemanticAttributes(semanticId);
    return <StyledGroup ref={ref} role="group" {...semanticProps} {...rest} />;
  },
);

DropdownGroup.displayName = 'Dropdown.Group';

/* ─────────────────────────── Label ────────────────────────────────────── */

const StyledLabel = styled.div`
  padding: ${rem(4)} ${rem(12)};
  font-size: ${rem(12)};
  color: var(--ui-text-subtle, ${palette.SpanishGray});
  font-weight: 600;
`;

export interface DropdownLabelProps extends React.HTMLAttributes<HTMLDivElement> {
  semanticId?: string;
}

const DropdownLabel = React.forwardRef<HTMLDivElement, DropdownLabelProps>(
  function DropdownLabel({ semanticId, ...rest }, ref) {
    const semanticProps = getSemanticAttributes(semanticId);
    return <StyledLabel ref={ref} {...semanticProps} {...rest} />;
  },
);

DropdownLabel.displayName = 'Dropdown.Label';

/* ─────────────────────────── Compound export ──────────────────────────── */

export const Dropdown = {
  Root: DropdownRoot,
  Trigger: DropdownTrigger,
  Content: DropdownContent,
  CloseButton: DropdownCloseButton,
  Item: DropdownItem,
  Separator: DropdownSeparator,
  Group: DropdownGroup,
  Label: DropdownLabel,
};
